import { useEffect, useMemo } from "react";

import { appRoutes } from "routes";
import { useCategory, useRouter, useLoading, useAlgolia, useSettings, useUsers } from "hooks";
import MetaTags from "components/MetaTags";
import MainTitle from "components/MainTitle";
import CategoryHeader from "components/CategoryHeader";
import CategoryPresenter from "presenters/CategoryPresenter";
import { jjLogger } from "utils/logUtils";
import Banner from "components/Banner";

import LoadingSkeleton from "./components/LoadingSkeleton";
import Content from "./components/Content";

const InviteCategory = () => {
  const { createInitialUiStateFromSingle } = useAlgolia();
  const {
    settings: { algolia },
  } = useSettings();
  const { currentUserLoaded } = useUsers();
  const { loadInviteCategory, resetCategory, category } = useCategory();
  const hero = CategoryPresenter.hero(category);
  const { func: loadInviteCategoryWithLoader, isNotFinished } = useLoading(loadInviteCategory);
  const {
    history,
    location,
    query: { slug, subcategory },
  } = useRouter();

  const subcategories = new Map([
    ["thanksgiving-invitations-online-friendsgiving", "friendsgiving-invitations"],
    ["birthday-party-invitations-online-kids", "kids-birthday-invitations"],
    ["wedding-save-the-date", "save-the-date-invitations"],
    ["wedding-bachelor-party", "bachelor-party-invitations"],
    ["wedding-bachelorette-party", "bachelorette-party-invitations"],
    ["wedding-wedding-shower", "wedding-shower-invitations"],
  ]);

  const getSlug = () => {
    if (!subcategory) return slug;

    const subcategorySlug = subcategories.get(`${slug}-${subcategory}`);

    if (!subcategorySlug) {
      jjLogger.logError(
        `InviteCategory.js getSlug: Subcategory not found for category: ${slug} and subcategory: ${subcategory}`,
      );
      history.push({ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } });
    }

    return subcategorySlug;
  };

  useEffect(() => {
    loadInviteCategoryWithLoader(getSlug()).catch((error) => {
      jjLogger.logError(`InviteCategory.js useEffect: ${error}`);
      history.push({ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } });
    });
    return () => {
      resetCategory();
    };
  }, [slug, subcategory]);

  const initialUiState = useMemo(() => {
    if (!currentUserLoaded || !category || isNotFinished) return undefined;

    return createInitialUiStateFromSingle(algolia.indexName, {
      query: {
        query: CategoryPresenter.query(category),
      },
    });
  }, [category, currentUserLoaded, isNotFinished, createInitialUiStateFromSingle]);

  if (!category || isNotFinished || !initialUiState) return <LoadingSkeleton />;

  return (
    <>
      <CategoryHeader category={category} loading={isNotFinished} />
      <Banner hero={hero} loading={isNotFinished} />
      <Content category={category} initialUiState={initialUiState} indexName={algolia.indexName} />
      <MainTitle title={CategoryPresenter.metaTitle(category)} />
      <MetaTags>
        <meta name="keywords" content={CategoryPresenter.metaKeywords(category)} />
        <meta name="description" content={CategoryPresenter.metaDescription(category)} />
        <meta name="og:title" content={CategoryPresenter.metaTitle(category)} />
        <meta name="og:description" content={CategoryPresenter.metaDescription(category)} />
        {!CategoryPresenter.indexable(category) && <meta name="robots" content="noindex" />}
      </MetaTags>
    </>
  );
};

export default InviteCategory;
